import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import { useEffect, useRef } from 'react';

interface DuraflowCategoryProps {
  title: string;
  subtitle: string;
  heroImage: string;
  heroImageAlt: string;
  description: string;
  applications: string[];
  applications2?: string[];
  applications2Title?: string;
  types: { title: string; description: string }[];
  differentials: string[];
  applicationsTitle?: string;
  differentialsTitle?: string;
  midCTA?: { title: string; text: string; buttonText: string };
  ctaTitle?: string;
  ctaText?: string;
  ctaButtonText?: string;
}

function HeroSection({
  title,
  subtitle,
  heroImage,
  heroImageAlt,
}: {
  title: string;
  subtitle: string;
  heroImage: string;
  heroImageAlt: string;
}) {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!heroRef.current) return;
      const scrollY = window.scrollY;
      const opacity = Math.max(0, 1 - scrollY / 600);
      heroRef.current.style.opacity = String(opacity);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-[80vh] flex items-center overflow-hidden bg-gp-graphite"
    >
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt={heroImageAlt}
          className="w-full h-full object-cover opacity-50"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-gp-graphite via-gp-graphite/70 to-gp-graphite/50"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-gp-graphite via-transparent to-transparent"></div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-24">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-[2px] bg-gp-yellow"></div>
            <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">DURAFLOW</span>
          </div>

          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold text-white uppercase leading-[0.95] mb-8">
            {title.split(' ').slice(0, -1).join(' ')}
            <br />
            <span className="text-gp-yellow">{title.split(' ').slice(-1)[0]}</span>
          </h1>

          <p className="text-gray-300 text-lg md:text-xl max-w-xl leading-relaxed mb-10">
            {subtitle}
          </p>

          <a
            href="https://wa.me/5527993092922"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-sm font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
          >
            <div className="w-9 h-9 bg-gp-graphite rounded-full flex items-center justify-center group-hover:bg-white transition-colors duration-300">
              <i className="ri-whatsapp-line text-gp-yellow group-hover:text-gp-graphite w-5 h-5 flex items-center justify-center transition-colors duration-300"></i>
            </div>
            <span>Consultar Disponibilidade</span>
            <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
          </a>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <a href="#descricao" className="text-gray-500 hover:text-gp-yellow transition-colors duration-300">
          <i className="ri-arrow-down-line text-2xl w-8 h-8 flex items-center justify-center"></i>
        </a>
      </div>
    </section>
  );
}

function DescriptionSection({ description }: { description: string }) {
  return (
    <section id="descricao" className="relative bg-gp-graphite py-20 md:py-28 overflow-hidden">
      <div className="w-full px-4 sm:px-6 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-[2px] bg-gp-yellow"></div>
            <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Sobre a Categoria</span>
            <div className="w-12 h-[2px] bg-gp-yellow"></div>
          </div>
          <p className="text-gray-300 text-lg md:text-xl leading-relaxed">
            {description}
          </p>
        </div>
      </div>
    </section>
  );
}

function MidCTASection({ midCTA }: { midCTA: { title: string; text: string; buttonText: string } }) {
  return (
    <section className="relative bg-gp-graphite py-16 md:py-20 overflow-hidden">
      <div className="w-full px-4 sm:px-6 lg:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider mb-4">
            {midCTA.title}
          </h3>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-8">
            {midCTA.text}
          </p>
          <a
            href="https://wa.me/5527993092922"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-sm font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
          >
            <div className="w-9 h-9 bg-gp-graphite rounded-full flex items-center justify-center group-hover:bg-white transition-colors duration-300">
              <i className="ri-whatsapp-line text-gp-yellow group-hover:text-gp-graphite w-5 h-5 flex items-center justify-center transition-colors duration-300"></i>
            </div>
            <span>{midCTA.buttonText}</span>
            <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

function ApplicationsSection({ applications, sectionTitle }: { applications: string[]; sectionTitle?: string }) {
  return (
    <section className="relative bg-gp-graphite-light py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(232,185,42,0.3) 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        ></div>
      </div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
              <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Duraflow</span>
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-white uppercase tracking-wider">
              {sectionTitle || 'Aplicações'}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {applications.map((item, index) => (
              <div
                key={index}
                className="group flex items-center gap-4 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/40 rounded-lg px-6 py-5 transition-all duration-300 hover:bg-gp-graphite-lighter/50"
              >
                <div className="w-10 h-10 bg-gp-yellow/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-gp-yellow/20 transition-colors duration-300">
                  <i className="ri-check-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                </div>
                <span className="text-gray-200 text-sm md:text-base font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TypesSection({ types }: { types: { title: string; description: string }[] }) {
  return (
    <section className="relative bg-gp-graphite py-20 md:py-28 overflow-hidden">
      <div className="w-full px-4 sm:px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
              <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Nosso Portfólio</span>
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-white uppercase tracking-wider">
              Tipos e Soluções
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {types.map((type, index) => (
              <div
                key={index}
                className="group relative bg-gp-graphite-light border border-gp-graphite-lighter hover:border-gp-yellow/50 rounded-lg p-8 transition-all duration-500 hover:-translate-y-2"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gp-yellow scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left rounded-t-lg"></div>
                <div className="w-14 h-14 bg-gp-yellow/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-gp-yellow/20 transition-colors duration-300">
                  <i className="ri-settings-3-line text-gp-yellow w-7 h-7 flex items-center justify-center"></i>
                </div>
                <h3 className="text-white font-semibold text-lg md:text-xl mb-3">{type.title}</h3>
                <p className="text-gray-400 text-sm md:text-base leading-relaxed">{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function DifferentialsSection({ differentials, sectionTitle }: { differentials: string[]; sectionTitle?: string }) {
  return (
    <section className="relative bg-gp-graphite-light py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(232,185,42,0.3) 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        ></div>
      </div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
              <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Por que Duraflow</span>
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-white uppercase tracking-wider">
              {sectionTitle || 'Diferenciais'}
            </h2>
          </div>

          <div className="space-y-4">
            {differentials.map((item, index) => (
              <div
                key={index}
                className="group flex flex-col md:flex-row md:items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/40 rounded-lg p-6 md:p-8 transition-all duration-300 hover:bg-gp-graphite-lighter/30"
              >
                <div className="w-12 h-12 bg-gp-yellow/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-gp-yellow/20 transition-colors duration-300">
                  <i className="ri-add-line text-gp-yellow w-6 h-6 flex items-center justify-center"></i>
                </div>
                <p className="text-gray-300 text-sm md:text-base leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CTASection({
  title,
  text,
  buttonText,
}: {
  title?: string;
  text?: string;
  buttonText?: string;
}) {
  return (
    <section className="relative min-h-[500px] md:min-h-[600px] flex items-center overflow-hidden bg-gp-graphite">
      <div className="absolute inset-0">
        <img
          src="/images/EQUIPAMENTOS-E-COMPONENTES/duraflow-cta.jpg"
          alt="Ambiente industrial Duraflow"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gp-graphite/70"></div>
      </div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 py-20">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-[2px] bg-gp-yellow"></div>
            <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Atendimento Técnico</span>
            <div className="w-12 h-[2px] bg-gp-yellow"></div>
          </div>

          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold text-white uppercase tracking-wider mb-6">
            {title ? (
              <>
                {title.split(' ').slice(0, -1).join(' ')}
                <br />
                <span className="text-gp-yellow">{title.split(' ').slice(-1)[0]}</span>
              </>
            ) : (
              <>
                Precisa de um
                <br />
                <span className="text-gp-yellow">Orçamento?</span>
              </>
            )}
          </h2>

          <p className="text-gray-300 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            {text || 'Nossa equipe técnica está pronta para entender sua aplicação e indicar a melhor solução hidráulica.'}
          </p>

          <a
            href="https://wa.me/5527993092922"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
          >
            <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center group-hover:bg-white transition-colors duration-300">
              <i className="ri-whatsapp-line text-gp-yellow group-hover:text-gp-graphite w-5 h-5 flex items-center justify-center transition-colors duration-300"></i>
            </div>
            <span>{buttonText || 'Chamar no WhatsApp'}</span>
            <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

export default function DuraflowCategoryPage({
  title,
  subtitle,
  heroImage,
  heroImageAlt,
  description,
  applications,
  applications2,
  applications2Title,
  types,
  differentials,
  applicationsTitle,
  differentialsTitle,
  midCTA,
  ctaTitle,
  ctaText,
  ctaButtonText,
}: DuraflowCategoryProps) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="min-h-screen bg-gp-graphite">
      <Navbar />
      <main>
        <HeroSection
          title={title}
          subtitle={subtitle}
          heroImage={heroImage}
          heroImageAlt={heroImageAlt}
        />
        <DescriptionSection description={description} />
        {midCTA && <MidCTASection midCTA={midCTA} />}
        <ApplicationsSection applications={applications} sectionTitle={applicationsTitle} />
        {applications2 && applications2.length > 0 && (
          <ApplicationsSection applications={applications2} sectionTitle={applications2Title || 'Aplicações'} />
        )}
        <TypesSection types={types} />
        <DifferentialsSection differentials={differentials} sectionTitle={differentialsTitle} />
        <CTASection title={ctaTitle} text={ctaText} buttonText={ctaButtonText} />
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}